import { Check, Facebook, Link2, Mail, MessageCircle, Share, Twitter } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/*
 * Share action for events, horses and lots.
 * Mobile (touch + Web Share API): opens the native share sheet directly.
 * Desktop: elegant dropdown — WhatsApp, Facebook, X, Email, Copy link.
 */
export const ShareMenu = ({ title, url, className = "", testid = "share-menu", variant = "pill" }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    toast(t("share.copied"));
    setTimeout(() => setCopied(false), 2000);
  };

  const nativeShare = async () => {
    try {
      await navigator.share({ title, url: shareUrl });
      return true;
    } catch {
      return true; // user cancelled — nothing else to do
    }
  };

  const isTouch = typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;
  const canNative = typeof navigator !== "undefined" && Boolean(navigator.share);

  const triggerCls =
    variant === "icon"
      ? `inline-flex items-center justify-center w-8 h-8 rounded-full text-[#0A0A0A]/60 hover:text-[#0A0A0A] hover:bg-black/5 transition-colors duration-200 ${className}`
      : `inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full text-sm font-medium text-[#0A0A0A] bg-black/[0.04] hover:bg-black/[0.08] transition-colors duration-200 ${className}`;

  if (isTouch && canNative) {
    return (
      <button type="button" onClick={nativeShare} data-testid={testid} className={triggerCls} aria-label={t("share.share")}>
        <Share className="w-3.5 h-3.5" aria-hidden="true" />
        {variant !== "icon" && t("share.share")}
      </button>
    );
  }

  const enc = encodeURIComponent;
  const targets = [
    { label: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${enc(`${title} — ${shareUrl}`)}` },
    { label: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}` },
    { label: "X", icon: Twitter, href: `https://twitter.com/intent/tweet?url=${enc(shareUrl)}&text=${enc(title || "")}` },
    { label: t("share.email"), icon: Mail, href: `mailto:?subject=${enc(title || "")}&body=${enc(shareUrl)}` },
  ];

  return (
    /* iOS Safari: controlled open + click toggle (see LanguageSwitcher). */
    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
      <DropdownMenuTrigger
        data-testid={testid}
        className={triggerCls}
        aria-label={t("share.share")}
        onPointerDown={(e) => e.preventDefault()}
        onClick={() => setMenuOpen((o) => !o)}
      >
        <Share className="w-3.5 h-3.5" aria-hidden="true" />
        {variant !== "icon" && t("share.share")}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-xl border-black/5 shadow-[0_16px_40px_rgb(0,0,0,0.08)] min-w-[11rem]">
        {targets.map(({ label, icon: Icon, href }) => (
          <DropdownMenuItem key={label} asChild className="rounded-lg cursor-pointer">
            <a href={href} target="_blank" rel="noreferrer" data-testid={`${testid}-${label.toLowerCase()}`}>
              <Icon className="w-4 h-4 mr-2 text-[#737373]" aria-hidden="true" />
              {label}
            </a>
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem onClick={copy} data-testid={`${testid}-copy`} className="rounded-lg cursor-pointer">
          {copied ? (
            <Check className="w-4 h-4 mr-2 text-[#1D4D3B]" aria-hidden="true" />
          ) : (
            <Link2 className="w-4 h-4 mr-2 text-[#737373]" aria-hidden="true" />
          )}
          {t("share.copyLink")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
